"use client";

import {
  ReactNode,
  RefObject,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingTree,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import clsx from "clsx";

export type DropdownDismissRef = RefObject<(() => void) | null>;

export default function Dropdown({
  placement,
  menu,
  dismissRef,
  onToggleOpen,
  className,
  children,
}: Readonly<{
  placement?: Placement;
  menu: ReactNode;
  dismissRef?: DropdownDismissRef;
  onToggleOpen?: (open: boolean) => void;
  className?: string;
  children: ReactNode;
}>) {
  const [isOpen, setIsOpen] = useState(false);

  const onOpenChange = useCallback(
    (open: boolean) => {
      onToggleOpen?.(open);
      setIsOpen(open);
    },
    [onToggleOpen],
  );

  const nodeId = useFloatingNodeId();
  const {
    refs: { setReference, setFloating },
    floatingStyles,
    context,
  } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange,
    middleware: [flip(), shift({ padding: 12 })],
    whileElementsMounted: autoUpdate,
    placement,
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  useImperativeHandle(dismissRef, () => () => setIsOpen(false));

  return (
    <FloatingTree>
      <div
        className={clsx("inline-block", className)}
        ref={setReference}
        {...getReferenceProps()}
        data-component="Dropdown"
      >
        {children}
      </div>
      {isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <div
            ref={setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-(--zindex-dropdown)"
            data-component="Dropdown-inner"
          >
            {menu}
          </div>
        </FloatingFocusManager>
      )}
    </FloatingTree>
  );
}
